import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Store, MapPin, Star, BadgeCheck, Shield } from 'lucide-react';
import { API } from '../App';

export default function VendorStorefront() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API}/vendors/storefront/${slug}`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          if (e?.response?.status === 404) setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 max-w-6xl mx-auto px-6 md:px-12 py-12 w-full">
          <div className="h-40 bg-muted rounded-lg animate-pulse mb-6" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {['s1','s2','s3','s4'].map((k) => (
              <div key={k} className="h-72 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 max-w-3xl mx-auto px-6 md:px-12 py-20 w-full text-center" data-testid="storefront-not-found">
          <Store className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-3xl font-heading font-semibold mb-3">Storefront not found</h1>
          <p className="text-muted-foreground mb-6">
            We couldn&apos;t find a vendor at <code className="bg-muted px-2 py-1 rounded text-sm">/store/{slug}</code>.
            The link may be outdated, or the vendor may have closed their store.
          </p>
          <Link to="/products" className="text-primary underline font-medium">
            Browse all products on Christ Kingdom →
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const { vendor, products, total_products } = data;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 max-w-6xl mx-auto px-6 md:px-12 py-10 w-full">
        {/* Storefront Hero */}
        <section
          data-testid="storefront-hero"
          className="bg-gradient-to-br from-primary/10 via-primary/5 to-amber-50/40 border border-primary/20 rounded-2xl p-6 md:p-8 mb-10"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/15 rounded-2xl flex items-center justify-center shrink-0">
              <Store className="h-9 w-9 md:h-10 md:w-10 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1
                  className="text-2xl md:text-3xl font-heading font-semibold"
                  data-testid="storefront-business-name"
                >
                  {vendor.business_name}
                </h1>
                {vendor.verification === 'verified' && (
                  <Badge className="bg-green-100 text-green-800 gap-1">
                    <BadgeCheck className="h-3 w-3" />
                    Verified
                  </Badge>
                )}
                {vendor.verification === 'trusted' && (
                  <Badge className="bg-amber-100 text-amber-800 gap-1">
                    <Shield className="h-3 w-3" />
                    Trusted
                  </Badge>
                )}
                {vendor.is_founding_vendor && (
                  <Badge
                    className="gap-1 bg-gradient-to-r from-amber-500 to-amber-700 text-white border-0"
                    data-testid="storefront-founding-badge"
                  >
                    <Star className="h-3 w-3 fill-white" />
                    Founding Vendor
                    {typeof vendor.founding_vendor_number === 'number' ? ` #${vendor.founding_vendor_number}` : ''}
                  </Badge>
                )}
              </div>
              {vendor.description && (
                <p className="text-muted-foreground text-sm md:text-base mb-2 max-w-3xl">
                  {vendor.description}
                </p>
              )}
              <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                {vendor.country && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {vendor.country}
                  </span>
                )}
                <span>
                  {total_products} {total_products === 1 ? 'listing' : 'listings'}
                </span>
                <span className="text-emerald-700 font-medium">
                  Verified on Christ Kingdom Marketplace
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Products grid */}
        {products.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground">
              <Store className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">This vendor hasn&apos;t listed any products yet.</p>
              <p className="text-sm mt-2">Check back soon!</p>
            </CardContent>
          </Card>
        ) : (
          <div>
            <h2 className="text-xl font-heading font-semibold mb-4">All Listings</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="storefront-products">
              {products.map((p) => (
                <ProductCard key={p.product_id} product={p} />
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
